import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Printer, FileText, Rows3, Columns3 } from 'lucide-react';
import { PrintTemplate } from './PrintTemplate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simplified output types - only 2 options
const OUTPUT_TYPES = [
  { id: 'with-key', name: 'Tracking Sheet + Score Key', description: 'Score grid with rubric key at top' },
  { id: 'full-rubric', name: 'Full Rubric Printout', description: 'Complete rubric with all descriptors' }
];

// Layout types for tracking sheets
const LAYOUT_TYPES = [
  { id: 'domain-rows-date-columns', name: 'Domains as Rows', icon: Rows3, description: 'Date columns across top' },
  { id: 'date-rows-domain-columns', name: 'Dates as Rows', icon: Columns3, description: 'Domain columns across top' }
];

const SPACING_OPTIONS = [
  { id: 'compact', name: 'Compact', description: 'Maximum entries' },
  { id: 'standard', name: 'Standard', description: 'Balanced spacing' },
  { id: 'spacious', name: 'Spacious', description: 'Easy to write' }
];

export const TrackingSheetGenerator = ({ rubric }) => {
  const [outputType, setOutputType] = useState('with-key');
  const [layoutType, setLayoutType] = useState('domain-rows-date-columns');
  const [orientation, setOrientation] = useState('portrait');
  const [spacing, setSpacing] = useState('standard');
  const [includeNameField, setIncludeNameField] = useState(true);
  const [includeDateField, setIncludeDateField] = useState(false); // Off by default since session grid has dates
  const [includeTherapistField, setIncludeTherapistField] = useState(false);
  const [studentName, setStudentName] = useState('');
  const [therapistName, setTherapistName] = useState('');

  const handlePrint = () => {
    window.print();
  };

  if (!rubric) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <FileText className="w-16 h-16 text-muted-foreground/30 mb-4" strokeWidth={1} />
        <h3 className="text-lg font-medium text-muted-foreground">No Rubric Selected</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Select or create a rubric first to generate tracking sheets.
        </p>
      </div>
    );
  }

  // Props for print template
  const printConfig = {
    rubric,
    outputType,
    layoutType,
    orientation,
    spacing,
    includeNameField,
    includeDateField,
    includeTherapistField,
    studentName,
    therapistName
  };

  // Preview dimensions
  const previewAspect = orientation === 'landscape' ? '11 / 8.5' : '8.5 / 11';

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Controls Panel */}
        <div className="space-y-4 no-print">
          <Card className="soft-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Output Type</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {OUTPUT_TYPES.map((type) => (
                <button
                  key={type.id}
                  data-testid={`output-type-${type.id}`}
                  onClick={() => setOutputType(type.id)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${
                    outputType === type.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/30'
                  }`}
                >
                  <div className="font-medium text-sm">{type.name}</div>
                  <div className="text-xs text-muted-foreground">{type.description}</div>
                </button>
              ))}
            </CardContent>
          </Card>

          {outputType !== 'full-rubric' && (
            <Card className="soft-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Layout</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {LAYOUT_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      data-testid={`layout-type-${type.id}`}
                      onClick={() => setLayoutType(type.id)}
                      className={`w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3 ${
                        layoutType === type.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/30'
                      }`}
                    >
                      <Icon className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                      <div>
                        <div className="font-medium text-sm">{type.name}</div>
                        <div className="text-xs text-muted-foreground">{type.description}</div>
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          )}

          <Card className="soft-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Page Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Orientation</Label>
                <Select value={orientation} onValueChange={setOrientation}>
                  <SelectTrigger data-testid="orientation-select" className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait</SelectItem>
                    <SelectItem value="landscape">Landscape</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Spacing</Label>
                <Select value={spacing} onValueChange={setSpacing}>
                  <SelectTrigger data-testid="spacing-select" className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SPACING_OPTIONS.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <Label htmlFor="name-field">Student Name</Label>
                  <Switch
                    id="name-field"
                    data-testid="include-name-switch"
                    checked={includeNameField}
                    onCheckedChange={setIncludeNameField}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="date-field">Page Date Field</Label>
                  <Switch
                    id="date-field"
                    data-testid="include-date-switch"
                    checked={includeDateField}
                    onCheckedChange={setIncludeDateField}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="therapist-field">Therapist/SLP</Label>
                  <Switch
                    id="therapist-field"
                    data-testid="include-therapist-switch"
                    checked={includeTherapistField}
                    onCheckedChange={setIncludeTherapistField}
                  />
                </div>
              </div>

              {includeNameField && (
                <div className="space-y-2">
                  <Label>Pre-fill Student Name</Label>
                  <Input
                    data-testid="student-name-input"
                    value={studentName}
                    onChange={(e) => setStudentName(e.target.value)}
                    placeholder="Leave blank for handwritten"
                    className="rounded-xl"
                  />
                </div>
              )}

              {includeTherapistField && (
                <div className="space-y-2">
                  <Label>Pre-fill Therapist Name</Label>
                  <Input
                    data-testid="therapist-name-input"
                    value={therapistName}
                    onChange={(e) => setTherapistName(e.target.value)}
                    placeholder="Leave blank for handwritten"
                    className="rounded-xl"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Button
            onClick={handlePrint}
            data-testid="print-btn"
            className="w-full rounded-xl btn-interaction"
            size="lg"
          >
            <Printer className="w-5 h-5 mr-2" />
            Print / Export PDF
          </Button>
        </div>

        {/* Preview Panel */}
        <div className="lg:col-span-2 no-print">
          <div className="sticky top-4">
            <h3 className="font-semibold mb-3 text-sm text-muted-foreground">
              Print Preview ({orientation === 'landscape' ? '11" × 8.5"' : '8.5" × 11"'})
            </h3>
            <div 
              className="bg-white border border-gray-300 shadow-lg overflow-auto"
              style={{
                maxWidth: orientation === 'landscape' ? '800px' : '600px',
                aspectRatio: previewAspect,
                padding: '20px'
              }}
            >
              <PrintTemplate {...printConfig} isPreview={true} />
            </div>
          </div>
        </div>
      </div>

      {/* Actual print output - rendered at root level via portal would be ideal, 
          but for simplicity we render it here and use CSS to show only during print */}
      <PrintTemplate {...printConfig} isPreview={false} />
    </>
  );
};

export default TrackingSheetGenerator;
